import { PropsWithChildren, createContext, useContext, useEffect, useRef, useState } from 'react';

import { Loading } from 'common/components';
import { useTimeout } from 'common/hooks';
import { logger } from 'common/services';
import { monitoring } from 'common/services/monitoring';
import { assert, isEmbedded, isLocal } from 'common/utils';
import { clearMagicLinkCooldown, clearMagicLinkStartTime, getMagicLinkStartTime } from 'modules/authentication/utils';
import { keycloak } from 'modules/keycloak';
import ErrorPage from 'pages/ErrorPage';

import { LoginEmbeddedSkeleton } from './components';

const AUTH_CONNECTION_TIMEOUT = 15_000;

// Helper to categorize errors for better monitoring
const categorizeError = (error: unknown): string => {
  if (!(error instanceof Error)) return 'unknown';

  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  if (message.includes('network') || message.includes('fetch')) return 'network';
  if (message.includes('timeout')) return 'timeout';
  if (message.includes('cors')) return 'cors';
  if (message.includes('unauthorized') || message.includes('forbidden')) return 'auth';
  if (name.includes('typeerror')) return 'type_error';

  return 'other';
};

// Type-safe Navigator Connection API
interface NavigatorConnection {
  effectiveType?: 'slow-2g' | '2g' | '3g' | '4g';
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NavigatorConnection;
  mozConnection?: NavigatorConnection;
  webkitConnection?: NavigatorConnection;
}

// Helper to get connection information
const getConnectionInfo = () => {
  const nav = navigator as NavigatorWithConnection;
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection;

  return {
    effectiveType: connection?.effectiveType || 'unknown',
    downlink: connection?.downlink || null,
    rtt: connection?.rtt || null,
    saveData: connection?.saveData || false,
  };
};

type AuthContextContextValue = {
  keycloak: typeof keycloak;
  isAuthenticated: boolean;
};

export const AuthContext = createContext<AuthContextContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!keycloak.authenticated);
  const [isLoading, setIsLoading] = useState(true);
  const [authenticationError, setAuthenticationError] = useState<string>();
  const initializingRef = useRef(false);

  useEffect(() => {
    // Prevent double initialization in React 18 Strict Mode
    if (initializingRef.current) {
      return;
    }
    initializingRef.current = true;

    const initKeycloak = async () => {
      const startTime = performance.now();
      const connectionInfo = getConnectionInfo();

      try {
        // checkLoginIframe disabled due to third-party cookie blocking in modern browsers
        // See: NAT-195 for context
        const authenticated = await keycloak.init({
          onLoad: 'check-sso',
          checkLoginIframe: false,
          enableLogging: isLocal(),
        });

        const duration = performance.now() - startTime;

        // Log successful initialization with metrics
        logger.info('Keycloak initialization succeeded', {
          authenticated,
          durationMs: Math.round(duration),
          userAgent: navigator.userAgent,
          online: navigator.onLine,
          cookiesEnabled: navigator.cookieEnabled,
          isEmbedded: isEmbedded(),
          connection: connectionInfo,
        });

        setIsAuthenticated(authenticated);
        if (authenticated) {
          // Track magic-link login success if a flow was started in this browser. The marker lives in
          // localStorage (shared across tabs) so it survives the emailed link opening in a new tab —
          // the case the old sessionStorage marker dropped. The marker's PRESENCE is the sole gate: it
          // is written only at magic-link start, self-expires after the 1h attribution window, and is
          // cleared at every non-magic-link auth entry point (SSO/password/OTP/LinkExpired) — so a
          // marker still present at an authenticated check-sso means this session came from the
          // magic-link flow. We previously also required auth_time to be within 5 min of now, but that
          // wall-clock veto silently dropped genuine same-browser logins whose Keycloak auth_time was
          // older than the window (e.g. check-sso binding a longer-lived SSO session) — undercounting
          // login_success — while adding no attribution safety the marker does not already provide.
          const magicLinkStartTime = getMagicLinkStartTime();
          if (magicLinkStartTime) {
            const timeToLoginMs = Date.now() - magicLinkStartTime;
            monitoring.trackAction('auth.magic_link.login_success', {
              timeToLoginSeconds: Math.round(timeToLoginMs / 1000),
            });
            clearMagicLinkStartTime();
          }
          clearMagicLinkCooldown();
        }
        if (isEmbedded()) {
          window.parent.postMessage({ type: 'AUTHENTICATED' }, window.location.origin);
        }
        setIsLoading(false);
      } catch (error) {
        const duration = performance.now() - startTime;
        const errorCategory = categorizeError(error);

        // Log failure with full metrics
        logger.error(error, {
          context: 'keycloak_init_failed',
          errorCategory,
          durationMs: Math.round(duration),
          userAgent: navigator.userAgent,
          online: navigator.onLine,
          cookiesEnabled: navigator.cookieEnabled,
          isEmbedded: isEmbedded(),
          connection: connectionInfo,
        });

        // Set user-friendly error message
        if (error instanceof Error) {
          setAuthenticationError(error.message);
        } else {
          // Handle non-Error exceptions with a generic user-friendly message
          setAuthenticationError('Authentication service unavailable. Please try again later.');
        }
        setIsLoading(false);
      }
    };

    initKeycloak();
  }, []);

  useTimeout(() => {
    // Only trigger timeout if initialization is still in progress and not complete
    if (isLoading && !isAuthenticated) {
      const connectionInfo = getConnectionInfo();

      logger.error(new Error('Keycloak initialization timeout'), {
        context: 'keycloak_init_global_timeout',
        timeoutMs: AUTH_CONNECTION_TIMEOUT,
        userAgent: navigator.userAgent,
        online: navigator.onLine,
        cookiesEnabled: navigator.cookieEnabled,
        isEmbedded: isEmbedded(),
        connection: connectionInfo,
        protocol: window.location.protocol,
        referrer: document.referrer || 'none',
        screenResolution: `${screen.width}x${screen.height}`,
        windowSize: `${window.innerWidth}x${window.innerHeight}`,
      });
      setAuthenticationError('Could not connect to auth service');
      setIsLoading(false);
    }
  }, AUTH_CONNECTION_TIMEOUT);

  if (isEmbedded()) {
    return <LoginEmbeddedSkeleton />;
  }

  if (isLoading) {
    return <Loading />;
  }

  if (authenticationError) {
    return <ErrorPage message={authenticationError} />;
  }

  return <AuthContext.Provider value={{ keycloak, isAuthenticated }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  assert(ctx, 'useAuth must be used within a AuthProvider');
  return ctx;
};
